import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { IMAGES } from "../../data/store";

const REVEAL = {
  hidden: { y: "110%" },
  visible: (i = 0) => ({
    y: "0%",
    transition: {
      delay: 0.3 + i * 0.12,
      duration: 1.05,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const Line = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      variants={REVEAL}
      custom={delay}
      className={`block will-change-transform ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0px", "-60px"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-end"
    >
      {/* Parallax background image */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Interior de la tienda El Navegante Wester"
          className="h-full w-full object-cover"
        />
      </motion.div>

      {/* Layered overlays */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-10 bg-[#0A0908]"
      />
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-[#0A0908]/40 via-[#0A0908]/40 to-[#0A0908]" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#0A0908]/85 via-[#0A0908]/20 to-transparent" />

      {/* Top-left index frame */}
      <div className="absolute z-20 top-24 left-6 md:left-12 flex items-center gap-4 text-[#C89B3C]">
        <span className="h-px w-12 bg-[#C89B3C]" />
        <span className="text-[0.65rem] uppercase tracking-[0.5em]">
          Est. Hollister · CA
        </span>
      </div>
      <div className="absolute z-20 top-24 right-6 md:right-12 text-right text-[#C89B3C]">
        <span className="text-[0.65rem] uppercase tracking-[0.5em]">
          Vol. I · Colección
        </span>
      </div>

      {/* Main copy */}
      <motion.div
        style={{ y: textY }}
        initial="hidden"
        animate="visible"
        className="relative z-20 mx-auto max-w-7xl w-full px-6 md:px-12 pb-24 md:pb-36"
      >
        <div className="mb-6">
          <Line delay={0}>
            <span className="text-xs md:text-sm uppercase tracking-[0.5em] text-[#C89B3C]">
              — Estilo vaquero auténtico
            </span>
          </Line>
        </div>

        <h1
          data-testid="hero-title"
          className="font-serif font-black tracking-[-0.02em] leading-[0.9] text-[#F5F0E6] text-6xl sm:text-7xl md:text-8xl lg:text-[9.5rem]"
        >
          <Line delay={1}>El Navegante</Line>
          <Line delay={2} className="italic text-[#C89B3C]">
            Wester
          </Line>
        </h1>

        <div className="mt-8 max-w-xl">
          <Line delay={3}>
            <span className="text-base md:text-lg font-light text-[#D4B996] leading-relaxed">
              Botas de piel exótica, sombreros finos, sillas de montar y
              talabartería trabajada a mano.
            </span>
          </Line>
          <Line delay={4}>
            <span className="text-base md:text-lg font-light text-[#8A7D71] leading-relaxed">
              Calidad de rancho. Alma mexicana.
            </span>
          </Line>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#productos"
            data-testid="hero-explore-btn"
            className="group relative overflow-hidden bg-[#A66E38] text-[#F5F0E6] uppercase tracking-[0.25em] text-xs py-5 px-9 transition-colors duration-300 text-center"
          >
            <span className="relative z-10">Explorar Colección</span>
            <span className="absolute inset-0 bg-[#C89B3C] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
          </a>
          <a
            href="#contacto"
            data-testid="hero-visit-btn"
            className="group relative overflow-hidden border border-[#A66E38] text-[#D4B996] uppercase tracking-[0.25em] text-xs py-5 px-9 transition-colors duration-300 text-center hover:text-[#0A0908]"
          >
            <span className="relative z-10">Cómo Llegar</span>
            <span className="absolute inset-0 bg-[#C89B3C] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
          </a>
        </motion.div>
      </motion.div>

      {/* Bottom scroll cue */}
      <motion.a
        href="#productos"
        aria-label="Bajar"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9 }}
        className="absolute z-20 bottom-8 left-1/2 -translate-x-1/2 text-[#C89B3C] flex flex-col items-center gap-2"
      >
        <span className="text-[0.6rem] uppercase tracking-[0.5em]">Desliza</span>
        <ChevronDown className="animate-bounce" size={22} />
      </motion.a>
    </section>
  );
};
